import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { fittedCamera, zoomPreviewAt, type PreviewCamera } from './preview-viewport';
import { bindPreviewWheel } from './preview-interactions';

/** View-only camera. It never changes the raster, traced geometry or export settings. */
export function usePreviewViewport(enabled: boolean) {
  const viewport = useRef<HTMLDivElement>(null);
  const current = useRef<PreviewCamera>({ ...fittedCamera });
  const drag = useRef<{ id: number; x: number; y: number; camera: PreviewCamera } | null>(null);
  const [camera, setCamera] = useState(current.current);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [panning, setPanning] = useState(false);
  const update = (next: PreviewCamera) => { current.current = next; setCamera(next); };
  const fit = () => update({ ...fittedCamera });
  const zoom = (value: number) => update(zoomPreviewAt(current.current, value));

  useEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = viewport.current;
    if (!element || !enabled) return;
    return bindPreviewWheel(element, () => current.current, update, () => Boolean(drag.current));
  }, [enabled]);

  useEffect(() => {
    const stop = () => {
      const pointer = drag.current;
      drag.current = null;
      setPanning(false);
      if (pointer && viewport.current?.hasPointerCapture(pointer.id)) viewport.current.releasePointerCapture(pointer.id);
    };
    window.addEventListener('blur', stop);
    return () => window.removeEventListener('blur', stop);
  }, []);

  const endPan = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    setPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return {
    viewport, camera, size, panning, fit, zoom,
    onPointerDown(event: PointerEvent<HTMLDivElement>) {
      if (!enabled || event.button !== 0 || !event.isPrimary || drag.current) return;
      if (event.target instanceof Element && event.target.closest('button, input, select, a, textarea')) return;
      // A normal one-finger touch keeps page scrolling available. Mouse/pen drags pan the preview.
      if (event.pointerType === 'touch') return;
      event.preventDefault();
      event.currentTarget.focus({ preventScroll: true });
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, camera: current.current };
      setPanning(true);
    },
    onPointerMove(event: PointerEvent<HTMLDivElement>) {
      const start = drag.current;
      if (!start || start.id !== event.pointerId) return;
      if (event.buttons === 0) { endPan(event); return; }
      update({ ...start.camera, x: start.camera.x + event.clientX - start.x, y: start.camera.y + event.clientY - start.y });
    },
    onPointerUp: endPan,
    onPointerCancel: endPan,
    onLostPointerCapture: endPan,
    onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      if (!enabled || drag.current || event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;
      const step = event.shiftKey ? 120 : 40;
      const pan = ({ ArrowLeft: { x: step, y: 0 }, ArrowRight: { x: -step, y: 0 }, ArrowUp: { x: 0, y: step }, ArrowDown: { x: 0, y: -step } } as Record<string, { x: number; y: number }>)[event.key];
      if (pan) update({ ...current.current, x: current.current.x + pan.x, y: current.current.y + pan.y });
      else if (event.key === '+' || event.key === '=') zoom(current.current.zoom * 1.2);
      else if (event.key === '-' || event.key === '_') zoom(current.current.zoom / 1.2);
      else if (event.key === '0' || event.key === 'Home') fit();
      else return;
      event.preventDefault();
    },
  };
}
